import React, { useState, useEffect } from "react"
import { Button, Card, Form } from "react-bootstrap"
import axios from "axios"
import { localserver } from "../../../api/common"
import { showToast, ToastMessage } from "../../Shared/ToastMessage"
import { FormField } from "./FormField"

const UpdateAttendeeModal = ({
  show,
  onHide,
  attendeeDetails,
  ticketList,
  getAttendeesByEventID,
  type = "attendee",
  setUpdateType,
}) => {
  const [attendee, setAttendee] = useState({
    id: null,
    customFields: {},
    ticket: "",
    addOns: [],
  })
  const [guestCustomFields, setGuestCustomFields] = useState({})
  const [errors, setErrors] = useState({})

  // Parse form_details to ensure it's an array
  const parseFormDetails = (formDetails) => {
    if (typeof formDetails === "string") {
      try {
        return JSON.parse(formDetails)
      } catch (error) {
        console.error("Error parsing form_details:", error)
        return []
      }
    }
    return Array.isArray(formDetails) ? formDetails : []
  }

  // Parse form_data, guest_data, or buyer_details
  const parseData = (data) => {
    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data || "[]")
        return parsed[0]?.custom_fields || parsed.custom_fields || []
      } catch (error) {
        console.error("Error parsing data:", error)
        return []
      }
    }
    return Array.isArray(data) ? data : []
  }

  // Parse add_ons_data or add_ons from form_data/guest_data
  const parseAddOnsData = (data) => {
    if (typeof data === "string") {
      try {
        const parsed = JSON.parse(data || "[]")
        if (Array.isArray(parsed)) {
          return parsed[0]?.add_ons || parsed
        }
        return []
      } catch (error) {
        console.error("Error parsing add_ons_data:", error)
        return []
      }
    }
    return Array.isArray(data) ? data : []
  }

  const getFieldOptions = (fieldId) => {
    if (!attendeeDetails.ticket_data) return null

    try {
      const ticketData =
        typeof attendeeDetails.ticket_data === "string"
          ? JSON.parse(attendeeDetails.ticket_data)
          : attendeeDetails.ticket_data

      const formDetails = parseFormDetails(ticketData.form_details)
      const fieldDef = formDetails.find((f) => f.id === fieldId)
      return fieldDef?.options || null
    } catch (error) {
      console.error("Error extracting field options:", error)
      return null
    }
  }

  // Prepopulate form when attendeeDetails changes
  useEffect(() => {
    console.log("attendeeDetails:", attendeeDetails)
    if (attendeeDetails) {
      let parsedFormData = []
      let parsedGuestData = []
      let customFields = {}
      let guestCustomFields = {}
      let ticketId = ""
      let parsedAddOns = []

      // Check if buyer_details exists and use it for non-guest attendees
      const buyerDetails = attendeeDetails.buyer_details ? parseData(attendeeDetails.buyer_details) : []
      setUpdateType(attendeeDetails.buyer_details ? "buyer" : "attendee")

      if (attendeeDetails.isGuest) {
        parsedFormData = Array.isArray(attendeeDetails.custom_fields)
          ? attendeeDetails.custom_fields
          : parseData(attendeeDetails.guest_data)
        customFields = parsedFormData.reduce((acc, field) => {
          if (field && field.id && field.value !== undefined) {
            acc[field.id] = field.value || ""
          }
          return acc
        }, {})
        parsedGuestData = parseData(attendeeDetails.form_data)
      } else {
        // Prioritize buyer_details if available, otherwise use form_data
        parsedFormData = buyerDetails.length > 0 ? buyerDetails : parseData(attendeeDetails.form_data)
        parsedGuestData = parseData(attendeeDetails.guest_data)
        customFields = parsedFormData.reduce((acc, field) => {
          if (field && field.id && field.value !== undefined) {
            acc[field.id] = field.value || ""
          }
          return acc
        }, {})
        guestCustomFields = parsedGuestData.reduce((acc, field) => {
          if (field && field.id && field.value !== undefined) {
            acc[field.id] = field.value || ""
          }
          return acc
        }, {})
        ticketId = attendeeDetails.ticket_id || ""
        parsedAddOns = parseAddOnsData(attendeeDetails.add_ons_data)
      }

      setAttendee({
        id: attendeeDetails.id || null,
        customFields,
        ticket: ticketId,
        addOns: parsedAddOns.map((addOn) => ({
          id: addOn.addon_id,
          ticket_id: addOn.ticket_id,
          variant: addOn.variant_name,
          quantity: addOn.quantity,
        })),
      })
      setGuestCustomFields(guestCustomFields)
      setErrors({})
      console.log("attendee.customFields:", customFields)
      console.log("guestCustomFields:", guestCustomFields)
      console.log("attendee.addOns:", attendee.addOns)
    }
  }, [attendeeDetails])

  // Validation function for attendee and guest details
  const validateAttendee = () => {
    const newErrors = {}
    if (!attendeeDetails.isGuest) {
      const ticket = ticketList.find((t) => t.id === Number.parseInt(attendee.ticket))
      if (!ticket) {
        newErrors.ticket = "Please select a ticket"
      }

      const formDetails = parseFormDetails(ticket?.form_details)
      if (formDetails.length > 0) {
        formDetails.forEach((field) => {
          const value = attendee.customFields[field.id]
          const errorKey = field.id

          if (field.required && (!value || value.trim() === "")) {
            newErrors[errorKey] = `${field.label} is required`
          }

          if (field.type === "email" && value && value.trim() !== "") {
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
              newErrors[errorKey] = `Invalid email format for ${field.label}`
            }
          }
        })
      }
    } else {
      Object.entries(attendee.customFields).forEach(([key, value]) => {
        if (!value || value.trim() === "") {
          newErrors[key] = `${key.replace("_", " ").toUpperCase()} is required`
        }
        if (key.includes("email") && value && value.trim() !== "") {
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            newErrors[key] = "Invalid email format"
          }
        }
      })
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleAttendeeChange = (fieldId, value) => {
    setAttendee({
      ...attendee,
      customFields: {
        ...attendee.customFields,
        [fieldId]: value,
      },
    })
    setErrors((prev) => ({ ...prev, [fieldId]: null }))
  }

  const handleSubmit = async () => {
    if (!validateAttendee()) {
      showToast("Please fix the errors in the form", "error")
      return
    }

    if (!attendee.id) {
      showToast("No attendee selected for update", "error")
      return
    }

    try {
      let formData = []
      let guestData = []
      let ticket

      if (!attendeeDetails.isGuest) {
        ticket = ticketList.find((t) => t.id === Number.parseInt(attendee.ticket))
        const formDetails = parseFormDetails(ticket?.form_details)
        formData = [
          {
            custom_fields: Object.entries(attendee.customFields || {}).map(([id, value]) => {
              const field = formDetails.find((f) => f.id === id) ||
                parseData(attendeeDetails.buyer_details || attendeeDetails.form_data).find((f) => f.id === id) || {
                  id,
                  type: id,
                  label: id,
                  name: id,
                }
              return {
                id,
                type: field.type,
                label: field.label,
                name: field.name,
                value,
              }
            }),
            add_ons: attendee.addOns.map((addOn) => ({
              addon_id: addOn.id,
              variant_name: addOn.variant,
              quantity: addOn.quantity,
            })),
          },
        ]
        if (Object.keys(guestCustomFields).length > 0) {
          guestData = [
            {
              custom_fields: Object.entries(guestCustomFields || {}).map(([id, value]) => {
                const field = parseData(attendeeDetails.guest_data).find((f) => f.id === id) || {
                  id,
                  type: id,
                  label: id,
                  name: id,
                }
                return {
                  id,
                  type: field.type,
                  label: field.label,
                  name: field.name,
                  value,
                }
              }),
              add_ons: parseAddOnsData(attendeeDetails.guest_data),
            },
          ]
        }
      } else {
        guestData = [
          {
            custom_fields: Object.entries(attendee.customFields || {}).map(([id, value]) => {
              const field = (Array.isArray(attendeeDetails.custom_fields)
                ? attendeeDetails.custom_fields
                : parseData(attendeeDetails.guest_data)
              ).find((f) => f.id === id) || {
                id,
                type: id,
                label: id,
                name: id,
              }
              return {
                id,
                type: field.type,
                label: field.label,
                name: field.name,
                value,
              }
            }),
            add_ons: parseAddOnsData(attendeeDetails.guest_data),
          },
        ]
        formData = [
          {
            custom_fields: parseData(attendeeDetails.form_data).map((field) => ({
              id: field.id,
              type: field.type,
              label: field.label,
              name: field.name,
              value: field.value || "",
            })),
            add_ons: parseAddOnsData(attendeeDetails.form_data),
          },
        ]
      }

      // Construct payload based on type
      const payload =
        type === "attendee"
          ? {
              attendee_id: attendee.id,
              event_id: Number.parseInt(localStorage.getItem("eventId")) || attendeeDetails.event_id,
              form_data: JSON.stringify(formData),
              guest_data: JSON.stringify(guestData),
            }
          : {
              id: attendeeDetails.attendee_payment_id,
              event_id: Number.parseInt(localStorage.getItem("eventId")) || attendeeDetails.event_id,
              buyer_details: JSON.stringify(formData),
              first_name:
                attendee.customFields[
                  Object.keys(attendee.customFields).find(
                    (id) =>
                      formData[0].custom_fields.find((f) => f.id === id)?.name === "first_name" ||
                      formData[0].custom_fields.find((f) => f.id === id)?.label === "First Name",
                  )
                ] || "",
              email:
                attendee.customFields[
                  Object.keys(attendee.customFields).find(
                    (id) =>
                      formData[0].custom_fields.find((f) => f.id === id)?.name === "email_address" ||
                      formData[0].custom_fields.find((f) => f.id === id)?.label === "Email Address",
                  )
                ] || "",
            }

      console.log("Payload for update:", payload)

      // Uncomment to enable API call

      const response = await axios.post(
        `${localserver}/${type === "attendee" ? "update_attendee_details" : "buyer_update"}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("access_token")}`,
          },
        },
      )

      if (response.data.success) {
        showToast(
          `${attendeeDetails.isGuest ? "Guest" : type === "attendee" ? "Attendee" : "Buyer"} updated successfully`,
          "success",
        )
        getAttendeesByEventID()
        onHide()
      } else {
        showToast(
          `Failed to update ${attendeeDetails.isGuest ? "guest" : type === "attendee" ? "attendee" : "buyer"}`,
          "error",
        )
      }
    } catch (error) {
      showToast(
        `Error updating ${attendeeDetails.isGuest ? "guest" : type === "attendee" ? "attendee" : "buyer"}`,
        "error",
      )
      console.error(
        `Error updating ${attendeeDetails.isGuest ? "guest" : type === "attendee" ? "attendee" : "buyer"}:`,
        error,
      )
    }
  }

  const sidebarModalStyle = {
    sidebar: { height: "100%", overflowY: "auto" },
    header: {
      borderBottom: "1px solid #e9ecef",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
    },
    closeButton: { background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer" },
    body: { overflowY: "auto", flex: 1 },
    quantityControl: { display: "flex", alignItems: "center" },
    quantityButton: { padding: "5px", margin: "0 5px", cursor: "pointer" },
    quantityInput: { width: "40px", textAlign: "center", border: "1px solid #ced4da", borderRadius: "4px" },
  }

  if (!show) return null

  const parsedFormData = attendeeDetails.isGuest
    ? Array.isArray(attendeeDetails.custom_fields)
      ? attendeeDetails.custom_fields
      : parseData(attendeeDetails.guest_data)
    : parseData(attendeeDetails.buyer_details || attendeeDetails.form_data)

  return (
    <>
      <ToastMessage />
      <div
        className={`extraspace_search_div1 add_attendites_model search_div${show ? " open" : ""}`}
        style={sidebarModalStyle.sidebar}
      >
        <div className="card member_master_sidebar_scrool">
          <div className="member_edit_pop_flex mb-3">
            <h3>
              {attendeeDetails.isGuest ? "Update Guest" : type === "attendee" ? "Update Attendee" : "Update Buyer"}
            </h3>
            <button className="search_toggle btn btn-danger" onClick={onHide}>
              <i className="fa fa-times" />
            </button>
          </div>

          <div style={sidebarModalStyle.body}>
            <Card>
              <Card.Body>
                <Form>
                  {parsedFormData.length > 0 ? (
                    parsedFormData.map((field) => (
                      <FormField
                        key={field.id}
                        field={{
                          id: field.id,
                          name: field.name,
                          label: field.label,
                          type: field.type,
                          required: true,
                          options: getFieldOptions(field.id),
                        }}
                        value={attendee.customFields[field.id] || ""}
                        onChange={(value) => handleAttendeeChange(field.id, value)}
                        error={errors[field.id]}
                      />
                    ))
                  ) : (
                    <p className="text-center text-muted py-4">
                      {attendeeDetails.isGuest
                        ? "No guest fields available."
                        : type === "attendee"
                          ? "No attendee fields available."
                          : "No buyer fields available."}
                    </p>
                  )}
                </Form>
              </Card.Body>
            </Card>
          </div>

          <div className="footer_card">
            <Button className="btn btn-outline-default btn" onClick={onHide}>
              Back
            </Button>
            <Button variant="primary" onClick={handleSubmit}>
              Update
            </Button>
          </div>
        </div>
      </div>
    </>
  )
}

export default UpdateAttendeeModal
